import { StateCreator } from 'zustand';
import { TransformState } from '../types';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('transformStateSlice');

export interface TransformStateSlice {
  // State
  transformStates: Map<string, TransformState>;

  // Transform state management
  initializeTransformState: (nodeId: string, isNew: boolean) => void;
  updateTransformState: (nodeId: string, stateChanges: Partial<TransformState>) => void;
  cacheTransformValues: (nodeId: string, values: Record<string, unknown>, source: TransformState['source']) => void;
  markTransformAsModified: (nodeId: string, values: Record<string, unknown>) => void;
  getTransformState: (nodeId: string) => TransformState | null;
  invalidatePipelineTransformCache: (pipelineId: string) => void;
}

export const createTransformStateSlice: StateCreator<TransformStateSlice> = (set, get) => ({
  // Initial state
  transformStates: new Map(),

  initializeTransformState: (nodeId: string, isNew: boolean) => {
    logger.debug('Initialize transform state:', nodeId, isNew);
    const state = get();
    
    // Handle both Map and plain object (after localStorage rehydration)
    const currentStates = state.transformStates;
    const isMap = currentStates instanceof Map;
    const newStates = isMap ? new Map(currentStates) : new Map(Object.entries(currentStates || {}));
    
    newStates.set(nodeId, {
      isNew,
      isModified: false,
      cacheValid: false,
      source: isNew ? 'defaults' : 'file',
      lastFetched: undefined,
    });
    
    set({ transformStates: newStates });
  },

  updateTransformState: (nodeId: string, stateChanges: Partial<TransformState>) => {
    logger.debug('Update transform state:', nodeId, stateChanges);
    const state = get();
    
    // Handle both Map and plain object (after localStorage rehydration)
    const currentStates = state.transformStates;
    const isMap = currentStates instanceof Map;
    const statesMap = isMap ? currentStates : new Map(Object.entries(currentStates || {}));
    
    const existing = statesMap.get(nodeId);
    
    if (!existing) {
      logger.warn('Cannot update non-existent transform state:', nodeId);
      return;
    }
    
    const newStates = isMap ? new Map(currentStates) : new Map(Object.entries(currentStates || {}));
    newStates.set(nodeId, { ...existing, ...stateChanges });
    set({ transformStates: newStates });
  },

  cacheTransformValues: (nodeId: string, values: Record<string, unknown>, source: TransformState['source']) => {
    logger.debug('Cache transform values:', nodeId, source, Object.keys(values).length);
    const state = get();
    
    // Handle both Map and plain object (after localStorage rehydration)
    const currentStates = state.transformStates;
    const isMap = currentStates instanceof Map;
    const statesMap = isMap ? currentStates : new Map(Object.entries(currentStates || {}));
    
    const existing = statesMap.get(nodeId);
    
    if (!existing) {
      logger.warn('Cannot cache values for non-existent transform state:', nodeId);
      return;
    }
    
    const newStates = isMap ? new Map(currentStates) : new Map(Object.entries(currentStates || {}));
    newStates.set(nodeId, {
      ...existing,
      cacheValid: true,
      source,
      lastFetched: new Date().toISOString(),
    });
    set({ transformStates: newStates });
  },

  markTransformAsModified: (nodeId: string, values: Record<string, unknown>) => {
    logger.debug('Mark transform as modified:', nodeId, Object.keys(values).length);
    const state = get();
    
    // Handle both Map and plain object (after localStorage rehydration)
    const currentStates = state.transformStates;
    const isMap = currentStates instanceof Map;
    const statesMap = isMap ? currentStates : new Map(Object.entries(currentStates || {}));
    
    const existing = statesMap.get(nodeId);
    
    if (!existing) {
      logger.warn('Cannot mark non-existent transform state as modified:', nodeId);
      return;
    }
    
    const newStates = isMap ? new Map(currentStates) : new Map(Object.entries(currentStates || {}));
    newStates.set(nodeId, {
      ...existing,
      isModified: true,
      source: 'user',
    });
    set({ transformStates: newStates });
  },

  getTransformState: (nodeId: string) => {
    const state = get();
    
    // Handle both Map and plain object (after localStorage rehydration)
    const currentStates = state.transformStates;
    const isMap = currentStates instanceof Map;
    
    if (isMap) {
      return currentStates.get(nodeId) || null;
    } else {
      // Convert object to Map for lookup
      const statesMap = new Map(Object.entries(currentStates || {}));
      return statesMap.get(nodeId) || null;
    }
  },

  invalidatePipelineTransformCache: (pipelineId: string) => {
    logger.debug('Invalidate pipeline transform cache:', pipelineId);
    
    const state = get();
    
    // Handle both Map and plain object (after localStorage rehydration)
    const currentStates = state.transformStates;
    const isMap = currentStates instanceof Map;
    const newStates = isMap ? new Map(currentStates) : new Map(Object.entries(currentStates || {}));
    
    // Get the pipeline to find its nodes
    const pipelines = (state as any).pipelines || [];
    const pipeline = pipelines.find((p: any) => p.id === pipelineId);
    
    if (pipeline && pipeline.nodes) {
      // Remove transform states for all nodes in this pipeline
      pipeline.nodes.forEach((node: any) => {
        if (newStates.has(node.id)) {
          logger.debug('Removing transform state for node:', node.id);
          newStates.delete(node.id);
        }
      });
      
      logger.debug(`Cleared ${pipeline.nodes.length} transform states for pipeline:`, pipelineId);
    } else {
      // Fallback: if we can't find the pipeline, log a warning
      logger.warn('Could not find pipeline to clear transform cache:', pipelineId);
    }
    
    set({ transformStates: newStates });
  },
});

